/**
 * Walks an RQB RuleGroup tree and reports the first leaf-level error.
 *
 * Used by the AssignmentsTab Formik form-level `validate` prop to block submit
 * when the QB tree contains incomplete or type-incompatible rules. The QB's
 * own per-cell red indicators (gated on `submitCount > 0` via QueryBuilderContext)
 * highlight the offending cells once submit is attempted, so we only need a
 * single string here to flip Formik's `isValid` flag.
 *
 * Scope: structural completeness (Field / Operator / Value present, or the
 * `match` mode and body a quantified collection leaf carries in place of both),
 * value type compatibility for numeric fields, the `range` bound a provider
 * may declare on one, and the refusals the compiler makes on a date leaf's target
 * (the event time on a rule without an event, a precision marker its operands
 * cannot carry, a comparison or unit its precision refuses). Not a full
 * CEL-grammar check — the BE does that on save and returns a 400.
 */

import type { RuleGroupType, RuleType } from 'react-querybuilder';
import { defaultPlaceholderFieldName, defaultPlaceholderOperatorName } from 'react-querybuilder';

import {
  EXACT_REFUSED_OPERATORS,
  isAnchoredValue,
  isExactOnlyUnit,
  isOffsetShape,
  isReservedAnchor,
  TARGET_LABEL,
  targetFieldType,
} from './dateRelations';
import { isCountMatchMode } from './matchModes';
import { getNumericRange, isWithinNumericRange, numericRangeMessage } from './numericRange';
import {
  EXACT_OFFSET_UNITS,
  OFFSET_DIRECTIONS,
  PRECISION_DAY,
  PRECISION_EXACT,
  TRIGGERED_AT_ANCHOR,
  USER_COMPARISON_OPERATORS,
} from './operators';
import type { AnchoredDateValueShape, FieldDefinitionDto, FieldType, HierarchicalValueShape } from './types';
import {
  COLLECTION_ROWS_RECORDS,
  collectionElements,
  collectionRows,
  FIELD_TYPE,
  isCollectionMatchLeaf,
  VARIANT_HINT_KEY,
} from './types';

// Unary = no value cell. Scalar nullity (null/notNull) for scalar fields;
// list-emptiness (empty/notEmpty) for multiselect fields. Must match the
// operators registered in `operators.ts`.
const UNARY_OPERATORS = new Set(['null', 'notNull', 'empty', 'notEmpty']);
// Operators that expect a list value. `in`/`notIn` are the scalar-multi-value
// pair (e.g. "complexity in [low, med]"); `anyOf`/`noneOf` are the multiselect
// set-overlap pair (the multiselect-type guard below catches all-multiselect
// regardless of operator, but keeping these explicit covers the scalar-list case).
const MULTI_VALUE_OPERATORS = new Set(['in', 'notIn', 'anyOf', 'noneOf']);
const NUMERIC_FIELD_TYPES = new Set<FieldType>([FIELD_TYPE.Number, FIELD_TYPE.Monetary]);
const DATE_FIELD_TYPES = new Set<FieldType>([FIELD_TYPE.Date, FIELD_TYPE.Datetime]);
// Every unit any precision can count in; which of them a given leaf may use is checked separately.
const OFFSET_UNIT_NAMES = new Set(EXACT_OFFSET_UNITS.map((u) => u.name));
const OFFSET_DIRECTION_NAMES = new Set(OFFSET_DIRECTIONS.map((d) => d.name));
const PRECISIONS: ReadonlyArray<unknown> = [PRECISION_DAY, PRECISION_EXACT];

/** What the tree is validated *for*, beyond its own contents. */
export interface ValidateConditionsTreeOptions {
  /** Whether the rule has a triggering event. Without one, "When the event occurred" has no event
   *  time to be evaluated against, and the BE refuses it at save. Defaults to false. */
  hasTriggeringEvent?: boolean;
}

const isRuleGroup = (node: RuleGroupType | RuleType): node is RuleGroupType =>
  'rules' in node && Array.isArray((node as RuleGroupType).rules);

// The threshold a count mode compiles: `_compile_collection_leaf` takes an int, never a bool or a
// negative one. A JSON number with no fraction is an int on the BE.
const isWholeCount = (value: unknown): boolean => typeof value === 'number' && Number.isInteger(value) && value >= 0;

// The shape a quantified leaf's `match` body has to have for the compiler to read it at all: a
// group. Takes `unknown` rather than a node union because it is applied to a leaf's `value`, which
// is an operand everywhere else in this file.
const isRuleGroupValue = (value: unknown): value is RuleGroupType =>
  typeof value === 'object' && value !== null && Array.isArray((value as RuleGroupType).rules);

// Whether a group carries a condition anywhere in it — what `_compile_group` answers `None` for
// when it does not: an empty group, or one whose every child is itself such a group.
const holdsCondition = (group: RuleGroupType): boolean =>
  group.rules.some((child) => {
    const node = child as RuleGroupType | RuleType;
    return !isRuleGroup(node) || holdsCondition(node);
  });

/** The one leaf-level error an author cannot clear from the condition cells themselves.
 *
 *  Every other message here names something the author can go and fix in the row; this one
 *  names a field that is no longer offered, so the remedies are outside the Conditions box —
 *  which is why the Save button states it too, rather than leaving a click do nothing. */
export const UNKNOWN_FIELD_ERROR = "A condition uses a field that is no longer available for this rule's scope.";

/** Whether the tree names a path the registry does not serve — the leaf's own field, or the
 *  catalog field a date leaf is compared against.
 *
 *  The same walk `validateConditionsTree` does, answering the narrower question the dialog
 *  needs: this is the only validation failure whose remedy is not inside the condition row, so
 *  it is the only one worth explaining on the Save button. The reserved anchors are instants the
 *  evaluator supplies rather than paths, so they are never "unknown" ones. */
export const hasUnknownFieldPath = (tree: RuleGroupType | null | undefined, fields: FieldDefinitionDto[]): boolean => {
  if (!tree || fields.length === 0) return false;
  const fieldsByPath = Object.fromEntries(fields.map((f) => [f.path, f]));

  const leafNamesUnknownPath = (rule: RuleType): boolean => {
    if (!rule.field || rule.field === defaultPlaceholderFieldName) return false;
    if (!fieldsByPath[rule.field]) return true;
    const anchor: unknown = isAnchoredValue(rule.value) ? rule.value.anchor : undefined;
    if (typeof anchor !== 'string' || !anchor || isReservedAnchor(anchor)) return false;
    return !fieldsByPath[anchor];
  };

  const search = (node: RuleGroupType | RuleType): boolean =>
    isRuleGroup(node)
      ? node.rules.some((child) => search(child as RuleGroupType | RuleType))
      : leafNamesUnknownPath(node);

  return search(tree);
};

/**
 * A date leaf compared against an anchor — a point (`{anchor}`) or an offset (`{anchor, dir, n,
 * unit}`), either one optionally carrying a precision marker.
 *
 * Each guard mirrors one the BE compiler or save path enforces with a 400, so an incomplete or
 * contradictory row blocks submit instead of losing the whole dialog to a server-side rejection.
 * The author's own shape errors come first and the target's drift after, so the edit the author
 * can fix from the row is named ahead of the one they cannot.
 */
const validateAnchoredLeaf = (
  rule: RuleType,
  value: AnchoredDateValueShape,
  fieldDef: FieldDefinitionDto,
  fieldsByPath: Record<string, FieldDefinitionDto>,
  options: ValidateConditionsTreeOptions
): string | undefined => {
  const label = fieldDef.label;
  // The editor only offers a target on a non-variant date field, so these two catch a saved rule
  // whose field has since changed type or become type-guarded — the compiler rejects both. The
  // guard is the `variant` hint, which every type-guarded namespace carries (an involved-party
  // block, a communication channel group), so the messages below name the guard rather than one
  // of them.
  if (!DATE_FIELD_TYPES.has(fieldDef.field_type)) {
    return `"${label}" must be a date field to be compared against another date.`;
  }
  if (fieldDef.hints?.[VARIANT_HINT_KEY]) {
    return `"${label}" is scoped to one kind of record and cannot be compared against another date.`;
  }

  // Read off a widened view: the value is stored JSON, so the marker can be any token at all.
  // `undefined` is its absence — JSON has no way to spell it, so it never reaches the wire.
  const { precision } = value as { precision?: unknown };
  const isExact = precision === PRECISION_EXACT;
  const offset = isOffsetShape(value) ? value : null;

  // Any one offset key makes the value an offset, and the BE then requires all three — so a
  // value carrying some but not all of them fails here rather than passing as a point.
  if (offset) {
    if (typeof offset.n !== 'number' || !Number.isInteger(offset.n) || offset.n < 0) {
      return `"${label}" needs a whole, non-negative number.`;
    }
    if (!OFFSET_UNIT_NAMES.has(offset.unit)) {
      return `"${label}" needs a time unit.`;
    }
    if (!OFFSET_DIRECTION_NAMES.has(offset.dir)) {
      return `"${label}" needs a direction — before or after its target.`;
    }
  }
  if (precision !== undefined && !PRECISIONS.includes(precision)) {
    return `"${label}" has a comparison precision this editor does not recognise — pick its target again.`;
  }
  // Whole-day precision truncates to days, so an hour count there would silently mean days.
  if (offset && isExactOnlyUnit(offset.unit) && !isExact) {
    return `"${label}" can count in Hours only against a date and time — choose another unit or another target.`;
  }

  const { anchor } = value as { anchor?: unknown };
  if (typeof anchor !== 'string' || !anchor) {
    return `"${label}" needs something to compare against.`;
  }
  if (anchor === TRIGGERED_AT_ANCHOR && !options.hasTriggeringEvent) {
    return `"${label}" is compared to ${TARGET_LABEL.eventOccurred}, but this rule has no triggering event.`;
  }
  if (!isReservedAnchor(anchor)) {
    // A saved rule outlives the registry it was authored against (an LOB leaves scope, a provider
    // retires), so re-check the target the same way the leaf's own field is checked — a target
    // the catalog stopped serving is the same drift, and the save path recompiles it against the
    // same catalog.
    const anchorDef = fieldsByPath[anchor];
    if (!anchorDef) {
      return UNKNOWN_FIELD_ERROR;
    }
    if (!DATE_FIELD_TYPES.has(anchorDef.field_type)) {
      return `"${label}" must be compared against a date field.`;
    }
    if (anchorDef.hints?.[VARIANT_HINT_KEY]) {
      return `"${label}" cannot be compared against a field that is scoped to one kind of record.`;
    }
  }

  if (isExact) {
    // Exact means both sides keep their time of day; a date-only side has none to compare. The
    // editor re-derives the marker whenever a target is picked, which is the remedy both name.
    if (fieldDef.field_type !== FIELD_TYPE.Datetime) {
      return `"${label}" has no time of day, so it cannot be compared to the minute — pick its target again.`;
    }
    if (targetFieldType(anchor, fieldsByPath) !== FIELD_TYPE.Datetime) {
      const targetLabel = fieldsByPath[anchor]?.label ?? anchor;
      return `"${label}" cannot be compared to the minute against "${targetLabel}", which has no time of day — pick its target again.`;
    }
    if (EXACT_REFUSED_OPERATORS.includes(rule.operator)) {
      return `"${label}" is compared to the minute, where "is", "is not" and "exactly" cannot hold — choose a before or after comparison.`;
    }
  }
  return undefined;
};

/**
 * A user leaf compared against another user field — `{anchor: <path>}` and nothing else, since a
 * user id has no spelling an author could type. Mirrors `_compile_field_comparison_leaf`, which
 * refuses every shape rejected here with a 400.
 */
const validateUserLeaf = (
  rule: RuleType,
  fieldDef: FieldDefinitionDto,
  fieldsByPath: Record<string, FieldDefinitionDto>
): string | undefined => {
  const label = fieldDef.label;
  // The editor offers only the presence checks on a type-guarded user field, so this catches a
  // stored rule: `_compile_variant_leaf` refuses every `{anchor}` value.
  if (fieldDef.hints?.[VARIANT_HINT_KEY]) {
    return `"${label}" is scoped to one kind of record, so it can only be checked for whether it is empty.`;
  }
  if (!USER_COMPARISON_OPERATORS.has(rule.operator)) {
    return `"${label}" can only be compared with another user by "is" or "is not".`;
  }
  const value: unknown = rule.value;
  const anchor: unknown = isAnchoredValue(value) ? value.anchor : undefined;
  if (typeof anchor !== 'string' || !anchor || Object.keys(value as object).length !== 1) {
    return `"${label}" needs another user to compare against.`;
  }
  const anchorDef = fieldsByPath[anchor];
  if (!anchorDef) {
    return UNKNOWN_FIELD_ERROR;
  }
  if (anchorDef.field_type !== FIELD_TYPE.User) {
    return `"${label}" must be compared against another user field.`;
  }
  if (anchorDef.hints?.[VARIANT_HINT_KEY]) {
    return `"${label}" cannot be compared against a field that is scoped to one kind of record.`;
  }
  return undefined;
};

const validateLeaf = (
  rule: RuleType,
  fieldsByPath: Record<string, FieldDefinitionDto>,
  options: ValidateConditionsTreeOptions
): string | undefined => {
  // The placeholder is RQB's "nothing picked yet" on a row the author just added
  // (`autoSelectField={false}`), which is a different thing from a path the catalog stopped
  // serving — it has to keep blocking submit.
  if (!rule.field || rule.field === defaultPlaceholderFieldName) {
    return 'Every condition needs a Field selected.';
  }
  const fieldDef = fieldsByPath[rule.field];
  if (!fieldDef) {
    // The catalog is derived live from organization configuration while a stored tree is frozen
    // at authoring time, so a leaf can stop resolving without anyone editing the rule. The rule
    // keeps evaluating either way — the evaluator runs the stored CEL, never the catalog — but
    // the save path recompiles the whole tree against the catalog and would 400 on this path,
    // so the form blocks here and says why rather than letting Save fail at the server.
    return UNKNOWN_FIELD_ERROR;
  }
  // A collection leaf quantifies instead of comparing, so none of the checks below apply to it:
  // its `value` is a nested group of predicates on the collection's elements rather than an
  // operand, and its `operator` is a slot nothing reads — `_compile_collection_leaf` never looks
  // at it, because the quantifier is `match.mode`. That is why this branch sits above the
  // operator check: react-querybuilder writes no operator for a field whose operator list is
  // empty, and `_compile_node` dispatches a `match`-carrying leaf to `_compile_leaf` whether or
  // not the slot is there, so requiring one here would block the form on a leaf the BE compiles.
  //
  // What it does require is the `match` block, which is the compiler's own first rejection on
  // this path. Without one the tree is asking for a comparison against a list of maps, which has
  // no reading — the compiler will not read it as an implicit `some`, and neither does the row,
  // whose mode cell reads off `rule.match` rather than the default react-querybuilder hands its
  // editor, so it renders empty and carries the "Required" marker this message pairs with.
  if (fieldDef.field_type === FIELD_TYPE.Collection) {
    if (!isCollectionMatchLeaf(rule)) {
      return `"${fieldDef.label}" needs a match mode — a collection can only be quantified over.`;
    }
    // A count mode compares the number of matching rows with `match.threshold`, and the compiler
    // refuses a threshold that is not a whole, non-negative number before it reads the body. It is
    // checked where the field offers the count modes — a collection whose rows are records — which
    // is where the mode cell puts a box for the number; an emptied box is `null`, and saving it as
    // zero would state a count the author never chose.
    //
    // The `mode` token itself is not checked against the ones the compiler emits, the same way an
    // operator here is checked for presence and never for membership; a mode the field does not
    // offer (an import: a count on a grouped collection, a mode a later version stopped offering)
    // is the BE's 400, which says why, and the mode cell adds it to its own offer so the row states
    // it verbatim rather than rendering empty.
    if (
      isCountMatchMode(rule.match.mode) &&
      collectionRows(fieldDef) === COLLECTION_ROWS_RECORDS &&
      !isWholeCount(rule.match.threshold)
    ) {
      return `"${fieldDef.label}" needs a whole, non-negative number of rows to count.`;
    }
    // The body is the leaf's other half, and both shapes this rejects are a 400 from
    // `_compile_collection_leaf` — a non-group body ("must be a group of predicates on the
    // collection's elements") and, under the three quantifiers, a body holding no condition
    // ("holds no condition") — which is the server-side rejection this walk exists to pre-empt.
    // A count mode may leave its body empty, and then counts every row: "at least 2 injuries".
    // Its leaves are then walked in the collection's own vocabulary: they name elements rather
    // than catalog paths, so `fieldsByPath` alone cannot resolve one, which is what
    // `validateCollectionBody` supplies — and a body's element that is itself a collection comes
    // back through this branch with that vocabulary, so a match inside a match is validated the
    // same way one level further in.
    if (!isRuleGroupValue(rule.value) || (!isCountMatchMode(rule.match.mode) && !holdsCondition(rule.value))) {
      return `"${fieldDef.label}" needs at least one condition on its rows.`;
    }
    return validateCollectionBody(rule.value, fieldDef, fieldsByPath, options);
  }
  // The placeholder is rejected alongside the empty string for the same reason it is on the field
  // above: `autoSelectOperator={false}` leaves the cell on react-querybuilder's `'~'`, which is a
  // truthy string. At the root the value checks below caught the row anyway; inside a collection
  // body a `datetime` element reaches the offset branch, which reads the operator as a
  // comparison — so the check belongs here, where both paths pass through it.
  if (!rule.operator || rule.operator === defaultPlaceholderOperatorName) {
    return 'Every condition needs an Operator selected.';
  }

  if (UNARY_OPERATORS.has(rule.operator)) {
    return undefined;
  }

  // Ahead of the anchored branch, which reads every `{anchor}` as a date relation.
  if (fieldDef.field_type === FIELD_TYPE.User) {
    return validateUserLeaf(rule, fieldDef, fieldsByPath);
  }

  // A date leaf's value is a specific date (a plain string, checked below with every other
  // scalar) or a value anchored on another instant.
  if (isAnchoredValue(rule.value)) {
    return validateAnchoredLeaf(rule, rule.value, fieldDef, fieldsByPath, options);
  }

  const isMultiValue = MULTI_VALUE_OPERATORS.has(rule.operator) || fieldDef.field_type === FIELD_TYPE.Multiselect;

  if (fieldDef.field_type === FIELD_TYPE.Hierarchical) {
    const hv = rule.value as HierarchicalValueShape | null | undefined;
    if (!hv || typeof hv !== 'object' || hv.parent === null || hv.parent === undefined) {
      return `"${fieldDef.label}" needs a parent value.`;
    }
    if (Array.isArray(hv.parent)) {
      if (hv.parent.length === 0) {
        return `"${fieldDef.label}" needs at least one parent value.`;
      }
      // Multi-parent leaves can't carry children — different parents may have
      // different valid children. Compiler enforces this too, but block at
      // submit time so the BE 400 doesn't surprise the user.
      if (hv.children?.length) {
        return `"${fieldDef.label}" cannot specify a sub-type when multiple parents are selected.`;
      }
    } else if (!hv.parent) {
      return `"${fieldDef.label}" needs a parent value.`;
    }
    return undefined;
  }

  if (isMultiValue) {
    if (!Array.isArray(rule.value) || rule.value.length === 0) {
      return `"${fieldDef.label}" needs at least one value.`;
    }
    if (NUMERIC_FIELD_TYPES.has(fieldDef.field_type)) {
      const allNumeric = (rule.value as unknown[]).every(
        (v) => v !== '' && v !== null && v !== undefined && !Number.isNaN(Number(v))
      );
      if (!allNumeric) {
        return `"${fieldDef.label}" values must all be numbers.`;
      }
      // A stored leaf outlives the editor that wrote it, and the bound is a property of the datum:
      // a value outside it can never match, so it is a rule that silently does nothing.
      const range = getNumericRange(fieldDef.hints);
      if (range && !(rule.value as unknown[]).every((v) => isWithinNumericRange(v, range))) {
        return `"${fieldDef.label}" values are out of range. ${numericRangeMessage(range)}`;
      }
    }
    return undefined;
  }

  // Single-value branch.
  if (rule.value === undefined || rule.value === null || rule.value === '') {
    return `"${fieldDef.label}" needs a value.`;
  }
  if (NUMERIC_FIELD_TYPES.has(fieldDef.field_type)) {
    if (Number.isNaN(Number(rule.value))) {
      return `"${fieldDef.label}" value must be a number.`;
    }
    const range = getNumericRange(fieldDef.hints);
    if (range && !isWithinNumericRange(rule.value, range)) {
      return `"${fieldDef.label}" value is out of range. ${numericRangeMessage(range)}`;
    }
  }
  return undefined;
};

/** The collection's elements as the same `FieldDefinitionDto` the checks above take.
 *
 *  Derived from the `collection` hint block and the catalog — the same read the nested builder's
 *  own field list comes from — so a body is validated against exactly the vocabulary its picker
 *  offered. `path` is the element name relative to the row because that is the spelling a stored
 *  sub-leaf carries: `_rewrite_element_fields` adds the `_c0.` alias itself. An element's own
 *  `hints` come along, so a type-guarded element is refused a target here as the compiler refuses
 *  it inside the body. */
const collectionElementDefinitions = (
  fieldDef: FieldDefinitionDto,
  catalog: ReadonlyArray<FieldDefinitionDto>
): Record<string, FieldDefinitionDto> =>
  Object.fromEntries(
    collectionElements(fieldDef, catalog).map((element) => [
      element.name,
      {
        path: element.name,
        label: element.label || element.name,
        field_type: element.field_type,
        enum_values: element.enum_values ?? null,
        group: fieldDef.group,
        source: fieldDef.source,
        hints: element.hints ?? {},
      } satisfies FieldDefinitionDto,
    ])
  );

/** The predicates a quantified leaf holds, validated as leaves in their own right.
 *
 *  They were read-only until this ticket, so the walk could stop at the group; now that an author
 *  can add a row inside the body, every incomplete shape the root rejects is reachable one level
 *  down and arrives at the server as a 400 — a placeholder field as "'~' is not an element of the
 *  collection", a missing operand as the compiler's own leaf rejection.
 *
 *  Element names resolve *over* the catalog rather than instead of it: a `datetime` element's
 *  target is a catalog path, since the target picker inside a match body offers the registry's
 *  date fields, and that is the one operand in the body resolved against the catalog. Nothing is
 *  shadowed — an element name starts with a segment of the row's own (`direction`, `api`,
 *  `variant`), and a catalog path starts with a context root (`claim`, `involved_party`, `event`),
 *  which no row namespaces its elements under. A field resolving only through the catalog half is
 *  still rejected, before the merged map is consulted: it is a leaf the compiler answers with "not
 *  an element of the collection". */
const validateCollectionBody = (
  body: RuleGroupType,
  fieldDef: FieldDefinitionDto,
  fieldsByPath: Record<string, FieldDefinitionDto>,
  options: ValidateConditionsTreeOptions
): string | undefined => {
  const elementsByName = collectionElementDefinitions(fieldDef, Object.values(fieldsByPath));
  const bodyFieldsByPath = { ...fieldsByPath, ...elementsByName };

  const walkBody = (node: RuleGroupType | RuleType): string | undefined => {
    if (isRuleGroup(node)) {
      for (const child of node.rules) {
        const err = walkBody(child as RuleGroupType | RuleType);
        if (err) return err;
      }
      return undefined;
    }
    if (node.field && node.field !== defaultPlaceholderFieldName && !elementsByName[node.field]) {
      return `"${fieldDef.label}" has a condition on "${node.field}", which is not one of its fields.`;
    }
    return validateLeaf(node, bodyFieldsByPath, options);
  };

  return walkBody(body);
};

const walk = (
  node: RuleGroupType | RuleType,
  fieldsByPath: Record<string, FieldDefinitionDto>,
  options: ValidateConditionsTreeOptions
): string | undefined => {
  if (isRuleGroup(node)) {
    for (const child of node.rules) {
      const err = walk(child as RuleGroupType | RuleType, fieldsByPath, options);
      if (err) return err;
    }
    return undefined;
  }
  return validateLeaf(node, fieldsByPath, options);
};

export const validateConditionsTree = (
  tree: RuleGroupType | null | undefined,
  fields: FieldDefinitionDto[],
  options: ValidateConditionsTreeOptions = {}
): string | undefined => {
  if (!tree) return undefined;
  // No registry yet (still loading) — skip validation rather than spuriously
  // blocking submit. The BE catches anything we miss.
  if (fields.length === 0) return undefined;
  const fieldsByPath = Object.fromEntries(fields.map((f) => [f.path, f]));
  return walk(tree, fieldsByPath, options);
};
